"use client"
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { classNames } from '@/lib/utils';
import { Table } from '@/types';
import { AreaChart, Area, Tooltip as ChartTooltip, ResponsiveContainer, CartesianGrid, XAxis, YAxis, BarChart, Bar, Cell } from 'recharts';
import moment from 'moment';
import { toast } from 'react-hot-toast';
import { DEMO_ACCOUNT_IDS } from '@/constants';
import { useGetAllUpdatesForAccount } from '@/utils/react-query-hooks';
const Tooltip = React.lazy(() => import('@/components/presentational/tailwind/TooltipWrapper'));

// Icons
import { TiZoomOut } from 'react-icons/ti';
import { ArrowSmallUpIcon, SparklesIcon } from '@heroicons/react/20/solid';
import TrendUpIcon from '@public/img/icons/trend-up-rec.svg';
import CameraIcon from '@public/img/icons/camera.svg';
import { InfoCircle } from 'src/app/app/icons/InfoCircle';
// import QuestionMarkIcon from '@public/img/icons/question-mark.svg';

export default function FollowersChart({ account, isAnalyticPage = false, updates: providedUpdates }: { account: Partial<Table<'accounts'>>, isAnalyticPage?: boolean, updates?: any[] }) {
	var { data: updates, error: fetchUpdatesError, isLoading: isLoadingInitialUpdates } = providedUpdates ? { data: providedUpdates, error: undefined, isLoading: false } : useGetAllUpdatesForAccount(account.id) || { data: undefined, error: undefined, isLoading: false };
	// console.log("🚀 ~ file: FollowersChart.tsx:31 ~ updates:", updates)

	// Last updated time
	const [lastUpdated, setLastUpdated] = useState<string | null>(null);
	useEffect(() => {
		if (isAnalyticPage) {
			const latestUpdate = updates[updates.length - 1].created_at;
			// if latestUpdate is today, show 1 hour ago, 2 hours ago, etc.
			if (moment(latestUpdate).isSame(moment(), 'day'))
				setLastUpdated("1 hour ago");
			else
				setLastUpdated(moment(latestUpdate).fromNow());
		}
	}, [updates]);

	// TODO: Implement this
	// const SHOW_PROJECTION = !account.ai_training_enabled;
	const SHOW_PROJECTION = false;

	const isDemoAccount = account && DEMO_ACCOUNT_IDS.includes(account.id);
	const [isHoveringChart, setIsHoveringChart] = useState<boolean>(false);
	const [hoveredArea, setHoveredArea] = useState<string | null>(null);
	const [hoveredBar, setHoveredBar] = useState(null);
	const [hoveredXAxisIndex, setHoveredXAxisIndex] = useState(-1);
	const handleMouseEnterXAxisIndex = (index) => {
		setHoveredXAxisIndex(index);
	};
	const handleMouseLeaveXAxisIndex = () => {
		setHoveredXAxisIndex(-1);
	};

	// Define tabs
	const tabs = isAnalyticPage ? ["Daily", "Over time"] : ['All Time', '30 Days', 'Daily'];
	const tabsRef = useRef<(HTMLElement | null)[]>([]);
	const [chartTimeFrameTab, setChartTimeFrameTab] = useState(0);
	const [timeFrameTabUnderlineWidth, setTimeFrameTabUnderlineWidth] = useState(0);
	const [timeFrameTabUnderlineLeft, setTimeFrameTabUnderlineLeft] = useState(0);
	useEffect(() => {
		const setTabPosition = () => {
			const currentTab = tabsRef.current[chartTimeFrameTab] as HTMLElement;
			setTimeFrameTabUnderlineLeft(currentTab?.offsetLeft ?? 0);
			setTimeFrameTabUnderlineWidth(currentTab?.clientWidth ?? 0);
		};
		setTabPosition();
	}, [chartTimeFrameTab, []]);
	const handleTabIndexChange = useCallback((index) => {
		console.log(index)
		setChartTimeFrameTab(index);
	}, []);

	// Data time frame
	const searchParams = useSearchParams();
	const [selectedTimeFrame, setSelectedTimeFrame] = useState<string>('365y');
	useEffect(() => {
		const dQueryParam = new URLSearchParams(window.location.search).get('d');
		const defaultTimeFrame = '365y';
		const timeFrame = dQueryParam ? dQueryParam : defaultTimeFrame;
		setSelectedTimeFrame(timeFrame as any);
	}, [searchParams]);
	let startDate = moment().subtract(30, 'days');
	const timeFrameValue = parseInt(selectedTimeFrame.slice(0, -1));
	const timeFrameUnit = selectedTimeFrame.slice(-1);
	switch (timeFrameUnit) {
		case 'd':
			startDate = moment().subtract(timeFrameValue, 'days');
			break;
		case 'm':
			startDate = moment().subtract(timeFrameValue, 'months');
			break;
		case 'y':
			startDate = moment().subtract(timeFrameValue, 'years');
			break;
		default:
			startDate = moment().subtract(30, 'days'); // Default to 30 days
	}

	const router = useRouter();
	const pathName = usePathname();
	const changeDateRange = (timeFrame: string) => {
		const newSearchParams = new URLSearchParams(window.location.search);
		timeFrame = timeFrame || '365y';
		newSearchParams.set('d', timeFrame);
		router.push(`${pathName}?${newSearchParams.toString()}`, undefined);
	}

	if (!updates || updates.length === 0) {
		updates = Array(2).fill({
			account_id: account.id,
			created_at: new Date().toISOString(),
			followers: account.followers,
			followings: 0,
			id: 'default-id',
		});
	} else if (updates && updates.length === 1) {
		// Add a default update to the beginning of the array
		updates.unshift({
			account_id: account.id,
			created_at: new Date().toISOString(),
			followers: account.followers,
			followings: 0,
			id: 'default-id',
		});
	}
	// const delta = updates[updates.length - 1].followers - updates[0].followers;
	const delta = ((account.followers - account.starting_followers) / account.starting_followers) * 100; // Percentage change in f
	const minFollowers = Math.min(...updates.map(update => update.followers));
	const maxFollowers = Math.max(...updates.map(update => update.followers));
	// Normalize the data
	const offset = 0.55;
	// console.log("🚀 ~ file: FollowersChart.tsx ~ filtering updates after", startDate.format('YYYY-MM-DD'));
	const normalizedUpdates = updates
		.filter(update => moment(update.created_at).isAfter(startDate))
		.map(update => {
			const normalizedFollowers = maxFollowers !== minFollowers ? (update.followers - minFollowers) / (maxFollowers - minFollowers) + offset : offset;
			return { ...update, followers: normalizedFollowers };
		});
	// console.log("🚀 ~ file: FollowersChart.tsx ~ updates", updates)
	// console.log("🚀 ~ file: FollowersChart.tsx ~ normalizedUpdates", normalizedUpdates)

	const chartData =
		// Check if it's demo account
		!isDemoAccount ? normalizedUpdates.map((item) => {
			const date = moment(item.created_at);
			const formattedDate = date.format('MMM D');
			const projection = item.followers * 1.3;
			return { name: formattedDate, followers: item.followers, projection };
		}) :

			// Demo data
			[...Array.from({ length: 22 }, (_, i) => {
				const startingFollowers = 4000
				const date = new Date();
				date.setDate(date.getDate() - (21 - i));
				const name = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
				const followers = startingFollowers + Math.round((Math.sin(i / 1.1) + 5) * 50 * i);
				const projection = followers * 1.3;
				return { name, followers, projection };
			})
			]
	// console.log("🚀 ~ file: FollowersChart.tsx ~ chartData", chartData)

	const ScreenshotButton = (
		<span className="tremor-Icon-root inline-flex flex-shrink-0 items-center bg-dashbg rounded-lg border-none cursor-pointer text-gray-500 hover:text-gray-700 mr-3 p-2.5 ignore"
			onClick={async () => {
				toast.loading('Generating follower growth chart...')
				// Generate chart image and download it
				const html2canvas = (await import('html2canvas')).default;
				const chartElement = document.getElementById('followers-growth-chart');
				if (chartElement) {
					html2canvas(chartElement, {
						ignoreElements: (node) => {
							// This will ignore the element if it has the 'ignore' class
							return node.classList.contains('ignore');
						},
						// imageTimeout: 0,
						// removeContainer: true,
					}).then(canvas => {
						const link = document.createElement('a');
						link.download = `${account.username}-followers-growth.png`;
						link.href = canvas.toDataURL();
						link.click();
						toast.dismiss()
						toast.success('Follower growth chart downloaded successfully!')
						console.log('Downloaded follower growth chart successfully!');
					});
				}
			}}>
			<CameraIcon className="w-5 h-5 tremor-Icon-icon shrink-0" />
		</span>
	)

	const CardHeaderTitle = ({ lastUpdated = null }) => (
		<div className='inline-flex flex-row items-center'>
			<div className="inline-flex items-center mb-1 text-base font-medium leading-normal text-zinc-900">
				<svg className="mr-2" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M13.3337 2.8898C14.5684 3.50343 15.417 4.77762 15.417 6.25C15.417 7.72238 14.5684 8.99657 13.3337 9.6102M15.0003 13.972C16.2599 14.5419 17.3941 15.4708 18.3337 16.6667M1.66699 16.6667C3.28907 14.6021 5.49131 13.3333 7.91699 13.3333C10.3427 13.3333 12.5449 14.6021 14.167 16.6667M11.667 6.25C11.667 8.32107 9.98806 10 7.91699 10C5.84592 10 4.16699 8.32107 4.16699 6.25C4.16699 4.17893 5.84592 2.5 7.91699 2.5C9.98806 2.5 11.667 4.17893 11.667 6.25Z" stroke="#85818E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
				</svg>

				Followers Growth
				{/* <QuestionMarkIcon className="w-[18px] h-[18px] ml-2 place-self-center ignore fill-gray-300" /> */}
				<InfoCircle className="w-5 h-5 ml-2 text-gray-400 place-self-center ignore" />
			</div>
			{lastUpdated && (
				<p className="hidden lg:block ml-1.5 text-sm italic text-gray-400 font-dashboard">
					–  Updated {lastUpdated}
				</p>
			)}
		</div>
	)

	return (
		<>
			{!updates || !chartData || isLoadingInitialUpdates ? (
				<div className="relative items-center justify-center block w-full h-full min-h-[382px]">
					{/* Skeleton loader */}
					{/* <h5 className="mb-2 text-2xl font-bold tracking-tight text-gray-900 opacity-20 dark:text-white">
            Followers Growth
          </h5>
          <p className="font-normal text-gray-700 opacity-20 dark:text-gray-400">
            Your Instagram account's follower growth evolution over the last 30 days.
          </p>
          <div role="status" className="absolute inset-0 flex items-center justify-center mt-40">
            <LoadingSpinner className="w-8 h-8 text-purple-500" />
            <span className="sr-only">Loading...</span>
          </div> */}
				</div>
			)
				: (
					<>
						{/* {JSON.stringify(updates)} */}
						<div className={classNames("block w-full mb-2 sm:flex",
							isAnalyticPage ? 'px-3 pt-2 pb-0 sm:px-1' : ''
						)}>
							<div className='w-full'>
								<React.Suspense fallback={<CardHeaderTitle lastUpdated={lastUpdated} />}>
									<Tooltip className="absolute left-10 ml-1 max-w-[300px] shadow-xl"
										content={"Your Instagram account's follower growth evolution over the last 30 days."}
										placement="auto"
										animation="duration-300">
										<CardHeaderTitle lastUpdated={lastUpdated} />
									</Tooltip>
								</React.Suspense>

								<div className="text-4xl font-bold text-gray-900 whitespace-nowrap">
									<span className={classNames("inline-flex items-center text-zinc-900 font-semibold leading-10",
										isAnalyticPage ? 'text-3xl' : 'text-4xl lg:text-[40px]'
									)}>
										{account.followers.toLocaleString()}
									</span>
									<span className={`ml-2.5 ${isAnalyticPage ? 'text-3xl' : 'text-4xl'} translate-y-1`}>🔥</span>
									<div className={`ml-4 ${delta == 0 ? 'align-middle' : 'mt-[3px]'} mb-1 align-middle inline-flex items-center rounded-full text-shadow px-2.5 py-0.5 text-sm font-medium text-white`}
										style={{ background: 'linear-gradient(74.41deg, #439917 -52.09%, #b0e752 170.9%)' }}
									>
										{delta == 0 && (
											<>
												<SparklesIcon className="self-center flex-shrink-0 w-3 h-3 mr-1 text-white" />
												Just started
											</>
										)}
										{delta > 0 && (
											<>
												<ArrowSmallUpIcon className="-ml-1 mr-0.5 h-5 w-5 flex-shrink-0 self-center text-white" />
												{/* {((updates[updates.length - 1].followers - updates[0].followers) / updates[0].followers * 100).toFixed(2)}% */}
												{delta < 10 ? delta.toFixed(2) : delta.toFixed(1)}%
											</>
										)}
										{delta < 0 && (
											<>
												<SparklesIcon className="self-center flex-shrink-0 w-3 h-3 mr-1 text-white" />
												Optimizing
											</>
										)}
									</div>
								</div>
							</div>
							<div className="flex w-full">
								{/* Tab Switch Button */}
								<div className={`relative inline-flex ${isAnalyticPage ? 'items-start' : 'items-center'} justify-start w-full mt-3 sm:justify-end sm:mt-0`}>

									{/* <React.Suspense fallback={ScreenshotButton}>
                    <Tooltip className="absolute -ml-1.5 max-w-[300px] shadow-xl"
                      content={"Download the image version of the follower growth chart"}
                      placement="bottom"
                      animation="duration-300">
                      {ScreenshotButton}
                    </Tooltip>
                  </React.Suspense> */}

									<div className={classNames('mx-auto flex flex-col items-center -ml-4 sm:-ml-6 lg:ml-0 lg:mr-0 overflow-scroll lg:w-auto w-[calc(100%_+_32px)] lg:overflow-hidden',
										isAnalyticPage ? 'px-0' : 'px-4 lg:px-0'
									)}>
										<div className="relative inline-flex self-start justify-start w-auto p-1 rounded-lg tremor-TabGroup-root flex-nowrap sm:self-end bg-[#F3F4F6] backdrop-blur-sm">
											<span className="absolute top-0 bottom-0 flex py-1 transition-all duration-300 rounded-lg -z-10" style={{ left: timeFrameTabUnderlineLeft, width: timeFrameTabUnderlineWidth }}>
												<span className="w-full h-full overflow-visible bg-white rounded shadow" />
											</span>
											{tabs.map((tab, index) => {
												const isActive = chartTimeFrameTab === index;
												return (
													<button
														key={index}
														ref={(el) => { tabsRef.current[index] = el; }}
														className={classNames(`${isActive ? `text-zinc-900` : `group hover:text-neutral-500 text-neutral-400`} font-medium my-auto cursor-pointer select-none rounded whitespace-nowrap text-sm text-center py-1.5 flex items-center justify-center`,
															tab === tabs[0] ? "px-4" : "px-4"
														)}
														onClick={handleTabIndexChange.bind(null, index)}
													>
														{tab}
													</button>
												);
											})}
										</div>
									</div>

								</div>
							</div>
						</div>

						<div id="followers-growth-chart" className={classNames("bg-white -mb-[6px]",
							isAnalyticPage && chartTimeFrameTab === 0 ? "-ml-6 w-[calc(100%_+_48px)]"
								: "w-full m-0"
						)}>
							{/* Follower Chart */}
							<ResponsiveContainer width="100%" height={265} key={chartTimeFrameTab} className="relative animate-fade-in">

								{isAnalyticPage && (!chartData || chartData.length === 0) ? (
									<>
										<div className="relative z-10 flex flex-col items-center justify-center pt-10 pb-14">
											<p className="text-lg font-medium text-gray-600 text-shadow font-dashboard">
												No data for the selected time range.
											</p>
											<p className="px-4 mt-1 text-base font-normal text-gray-500 font-dashboard">
												Try selecting a different time range or clearing your date filters. 🤞
											</p>

											<button className="border mt-4 shadow-xs group hidden items-center justify-center gap-2 rounded-xl border-1 border-gray-300 px-2.5 py-1.5 text-sm font-semibold text-gray-700 shadow bg-white transition-all duration-200 hover:bg-gray-50 hover:text-gray-900 focus:outline-none lg:inline-flex lg:px-3 lg:py-2"
												type="button"
												onClick={() => changeDateRange(null)}
											>
												<TiZoomOut className='w-5 h-5 text-green-500 transition-all duration-200 transform group-hover:-rotate-12' />
												Clear Filters
											</button>
										</div>

										<div className="absolute top-0 left-0 z-[9] w-full h-full pointer-events-none bg-gradient-to-t from-green-200 to-transparent" />
									</>
								) : (isAnalyticPage && chartTimeFrameTab === 0 ? (
									<BarChart
											className='mt-6'
										data={chartData}
										onMouseMove={state => {
											if (state.isTooltipActive)
												setHoveredBar(state.activeTooltipIndex);
											else
												setHoveredBar(null);
										}}
									>
										<defs>
												<linearGradient id="gradient-purple-dim" x1="0" y1="0" x2="1" y2="1" gradientTransform='rotate(15)'>
													<stop offset="10%" stopColor="#F4D19F" stopOpacity="0.85"></stop>
													<stop offset="60%" stopColor="#F94DDB" stopOpacity="0.85"></stop>
													<stop offset="110%" stopColor="#7549F4" stopOpacity="0.85"></stop>
											</linearGradient>

												<linearGradient id="gradient-purple" x1="0" y1="0" x2="1" y2="1" gradientTransform='rotate(15)'>
													<stop offset="10%" stopColor="#F4D19F" stopOpacity="1"></stop>
													<stop offset="60%" stopColor="#F94DDB" stopOpacity="1"></stop>
													<stop offset="110%" stopColor="#7549F4" stopOpacity="1"></stop>
												</linearGradient>
											</defs>
											<XAxis
												axisLine={true}
												dataKey="name"
												height={45}
												stroke='var(--color-gray-200)'
												strokeWidth={1}
												tick={(props) => {
													const { x, y, payload } = props;
													const isActive = hoveredXAxisIndex === chartData.findIndex((entry) => entry.name === payload.value);

													return (
														<g transform={`translate(${x},${y})`}>
															<text x={0} y={0} dy={22} textAnchor="middle"
																fill={isActive ? '#F826D3' : 'var(--color-gray-400)'}
																style={{ cursor: 'pointer', fontSize: 14, fontFamily: "Inter, system-ui" }}
																onMouseEnter={() => handleMouseEnterXAxisIndex(chartData.findIndex((entry) => entry.name === payload.value))}
																onMouseLeave={handleMouseLeaveXAxisIndex}
															>
																{moment(payload.value, 'MMM D').format('MM/DD')}
															</text>
														</g>
													);
												}}
											/>
											<CartesianGrid vertical={false} strokeDasharray="3 3" stroke="#ecebed" />
											<ChartTooltip
												cursor={false}
												content={({ active, payload, label }) => {
													if (active && payload && payload.length) {
														const data = payload.find(p => p.dataKey === hoveredArea);
														if (data) {
															// Reverse the normalization and offset for the tooltip value
															const date = data.payload.name;
															const updateForHoveredDate = normalizedUpdates.find((update: any) => moment(update.created_at).format('MMM D') === date);
															const updateForPreviousDay = normalizedUpdates.find((update: any) => moment(update.created_at).format('MMM D') === moment(date).subtract(1, 'days').format('MMM D'));
															const followersForHoveredDate = Math.round((Number(updateForHoveredDate.followers) - offset) * (maxFollowers - minFollowers) + minFollowers);
															// Calculate the daily change in followers from the previous day
															if (!updateForPreviousDay)
																return null;

															const followersForPreviousDay = Math.round((Number(updateForPreviousDay.followers) - offset) * (maxFollowers - minFollowers) + minFollowers);
															const followersDeltaChangeFromPreviousDay = followersForHoveredDate - followersForPreviousDay;

															return (
																<div className="flex flex-col items-start justify-between" style={{ fontSize: '14px', backgroundColor: '#fff', color: '#32325d', boxShadow: '0 1px 3px rgba(50, 50, 93, 0.15), 0 1px 0 rgba(0, 0, 0, 0.02)', borderRadius: '4px', padding: '10px' }}>
																	<div className="flex items-center font-dashboard">
																		<div className="w-3 h-3 mr-2 rounded-2xl" style={{ background: 'linear-gradient(40deg, #F4D19F 15%, #F94DDB 50%, #7549F4 130%)' }}></div>
																		<p className="pr-8 text-md" style={{ textAlign: 'left' }}>{`${label}`}</p>
																	</div>
																	<p className="pl-5 text-sm font-semibold" style={{ textAlign: 'left' }}>
																		Daily Change: {followersDeltaChangeFromPreviousDay > 0 ? `+${followersDeltaChangeFromPreviousDay.toLocaleString()}` : `${followersDeltaChangeFromPreviousDay.toLocaleString()}`}
																	</p>
																	<p className="pl-5 text-sm font-semibold text-zinc-500" style={{ textAlign: 'left' }}>
																		Total Followers: {followersForHoveredDate.toLocaleString()}
																	</p>
																</div>
															)
														}
													}

													return null;
												}}
												wrapperStyle={{ backgroundColor: 'white', borderRadius: '12px', 'overflow': 'hidden', color: 'var(--color-gray-900)', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', outline: "none" }}
											/>

											<Bar
												key={chartTimeFrameTab}
												dataKey={'followers'}
												strokeWidth={3}
												fillOpacity={1}
												maxBarSize={35}

												radius={[100, 100, 0, 0]}
												fill="url(#gradient-purple)"
												onMouseEnter={() => { setIsHoveringChart(true); setHoveredArea('followers'); }}
												onMouseLeave={() => { setIsHoveringChart(false); setHoveredArea(null); }}
											>
												{chartData.map((entry, index) => (
													<Cell key={`cell-${index}`} fill={hoveredBar === index ? 'url(#gradient-purple-dim)' : 'url(#gradient-purple)'}
														onMouseEnter={() => { setIsHoveringChart(true); setHoveredArea('followers'); }}
														onMouseLeave={() => { setIsHoveringChart(false); setHoveredArea(null); }}
													/>
												))}
											</Bar>
										</BarChart>

									) : (
										<AreaChart
												className={isAnalyticPage ? 'mt-6' : ''}
												margin={{ top: 0, left: -30, right: 0, bottom: 58 }}
												data={chartData}
											>
												<defs>
													{/* <linearGradient id="colorFollowers" x1="0" y1="0.4" x2="0" y2="1">
                            <stop offset="20%" stopColor="var(--color-indigo-600)" stopOpacity="0.1"></stop><stop offset="100%" stopColor="var(--color-indigo-600)" stopOpacity="0"></stop></linearGradient> */}
													{/* <linearGradient id="colorFollowers" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="var(--color-indigo-600)" stopOpacity={0.9} />
                <stop offset="95%" stopColor="var(--color-pink-200)" stopOpacity={0.4} />
              </linearGradient> */}
													<linearGradient id="colorFollowers" x1="0" y1="0" x2="1" y2="0">
														<stop offset="0%" stopColor="rgba(245, 133, 41, 0.15)" />
														<stop offset="25%" stopColor="rgba(252, 199, 55, 0.15)" />
														<stop offset="50%" stopColor="rgba(221, 42, 123, 0.15)" />
														<stop offset="75%" stopColor="rgba(129, 52, 175, 0.15)" />
														<stop offset="100%" stopColor="rgba(81, 91, 212, 0.15)" />
													</linearGradient>
													<linearGradient id="strokeFollowers" x1="0" y1="0" x2="1" y2="0">
														<stop offset="0%" stopColor="#FF7F1A" />
														<stop offset="25%" stopColor="#FFD700" />
														<stop offset="50%" stopColor="#F50057" />
														<stop offset="75%" stopColor="#9B27AF" />
														<stop offset="100%" stopColor="#3D5AFE" />
													</linearGradient>

													<linearGradient id="colorProjection" x1="0" y1="0" x2="900" y2="0">
														<stop offset="0%" stopColor="rgba(245, 133, 41, 0.08)" />
														<stop offset="25%" stopColor="rgba(252, 199, 55, 0.08)" />
														<stop offset="50%" stopColor="rgba(221, 42, 123, 0.08)" />
														<stop offset="75%" stopColor="rgba(129, 52, 175, 0.08)" />
														<stop offset="100%" stopColor="rgba(81, 91, 212, 0.08)" />
													</linearGradient>
													<linearGradient id="strokeProjection" x1="0" y1="0" x2="900" y2="0">
														<stop offset="0%" stopColor="rgba(245, 133, 41, 0.3)" />
														<stop offset="25%" stopColor="rgba(252, 199, 55, 0.3)" />
														<stop offset="50%" stopColor="rgba(221, 42, 123, 0.3)" />
														<stop offset="75%" stopColor="rgba(129, 52, 175, 0.3)" />
														<stop offset="100%" stopColor="rgba(81, 91, 212, 0.3)" />
													</linearGradient>
												</defs>
												<XAxis
													tickLine={false}
													axisLine={false}
													className="font-['Inter']"
													tick={{ fontSize: 12.5, fontFamily: "Inter, system-ui", fill: '#B4B6BB' }}
													dataKey="name"
													tickMargin={-30}
													mirror={true}
												/>
												<YAxis
													tickLine={false}
													axisLine={false}
													className="font-['Inter']"
													tick={{ fontSize: 12.5, fontFamily: "Inter, system-ui", fill: '#B4B6BB' }}
													dataKey="followers"
													tickMargin={5}
													orientation="left"
												/>
												<CartesianGrid vertical={false} stroke="#F3F4F6" />
												<ChartTooltip
													content={({ active, payload, label }) => {
														if (active && payload && payload.length) {
															const data = payload.find(p => p.dataKey === hoveredArea);
															if (data) {
																// Reverse the normalization and offset for the tooltip value
																const value = Math.round((Number(data.value) - offset) * (maxFollowers - minFollowers) + minFollowers);
																// const originalFollowers = Math.round((Number(payload[0].value) - offset) * (maxFollowers - minFollowers) + minFollowers);
																if (data.dataKey === 'followers')
																	return (
																		<div className="flex flex-col items-start justify-between" style={{ fontSize: '14px', backgroundColor: '#16171C', color: '#FFFFFF', boxShadow: '0 1px 3px rgba(50, 50, 93, 0.15), 0 1px 0 rgba(0, 0, 0, 0.02)', borderRadius: '6px', padding: '10px 14px' }}>
																			<div className="flex items-center">
																				{/* <div className="w-3 h-3 mr-2 bg-pink-500 rounded-2xl"></div> */}
																				<p className="pr-8 font-semibold text-md text-neutral-300" style={{ textAlign: 'left' }}>{`${label}`}</p>
																			</div>
																			<p className="text-sm font-medium" style={{ textAlign: 'left' }}>
																				{value.toLocaleString()} Followers
																			</p>
																		</div>
																	)

																else if (data.dataKey === 'projection')
																	return (
																		<div className="flex flex-col items-start justify-between text-white bg-gradient-to-br from-purple-500 to-purple-600 saturate-150"
																			style={{ fontSize: '14px', boxShadow: '0 1px 3px rgba(50, 50, 93, 0.15), 0 1px 0 rgba(0, 0, 0, 0.02)', borderRadius: '4px', padding: '10px' }}>
																			<div className="flex items-center font-dashboard">
																				<div className="w-3 h-3 mr-2 bg-green-300 rounded-2xl"></div>
																				<p className="pr-8 text-md" style={{ textAlign: 'left' }}>{`${label}`}</p>
																			</div>
																			<p className="pl-5 text-sm font-bold" style={{ textAlign: 'left' }}>
																				{value.toLocaleString()} Followers
																			</p>
																			<p className="pl-5 text-sm font-semibold drop-shadow-sm text-amber-200 saturate-150" style={{ textAlign: 'left' }}>
																				with <span className='font-bold font-dashboard'>AI Training</span>
																			</p>
																		</div >
																	)
															}
														}

														return null;
													}}
													wrapperStyle={{ backgroundColor: '#16171C', borderRadius: '6px', 'overflow': 'hidden', color: 'white', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', outline: "none" }}
												/>

												{/* Projection Growth Area */}
												{SHOW_PROJECTION && (
													<Area
														type="basis"
														dataKey="projection"
														stroke="url(#strokeProjection)"
														strokeDasharray="5"
														strokeWidth={2}
														fillOpacity={1}
														fill="url(#colorProjection)"
														onMouseEnter={() => setHoveredArea('projection')}
														onMouseLeave={() => setHoveredArea(null)}
													/>
												)}


												{/* Followers Growth Area  */}
												<Area
													key={chartTimeFrameTab}
													type="monotone"
													dataKey="followers"
													stroke={normalizedUpdates && normalizedUpdates.length === 2 && normalizedUpdates[0].followers === normalizedUpdates[1].followers ? "var(--color-orange-200)" : "url(#strokeFollowers)"}
											strokeWidth={2}
											fillOpacity={1}
											fill="url(#colorFollowers)"
											dot={{
												stroke: 'white', strokeOpacity: 1, strokeWidth: 3, r: 4, strokeDasharray: '', fill: "white", fillOpacity: 0.55, opacity: isHoveringChart ? 1 : 0,
												style: { filter: 'drop-shadow(0 0 3px rgba(0, 0, 0, 0.1))', transition: 'all 0.3s ease-out' }
											}}
											activeDot={{
												stroke: 'white', strokeOpacity: 1, strokeWidth: 4, r: 6, strokeDasharray: '', fill: "white", fillOpacity: 1, opacity: isHoveringChart ? 1 : 0,
												style: { filter: 'drop-shadow(0 0 3px rgba(0, 0, 0, 0.15))', transition: 'all 0.3s ease-out' }
											}}
											onMouseEnter={() => { setIsHoveringChart(true); setHoveredArea('followers'); }}
											onMouseLeave={() => { setIsHoveringChart(false); if (SHOW_PROJECTION) setHoveredArea(null); }}
										/>
									</AreaChart >
								))}


							</ResponsiveContainer>

							{/* <Image src='/img/placeholder/followers-chart-3.png' alt='World Map Chart Placeholder' width={800} height={250} /> */}
						</div >
					</>
				)}
		</>
	);
}
